import React from "react";
import {
  Button,
  Popup,
  ExternalLinks,
  CommonErrorPopup,
} from "component-library";
import { useTranslation, Trans } from "i18n";
import { AuthInstagramIntegrationCode } from "shared-types";
import { ReactComponent as ShieldIcon } from "component-library/src/images/svg/icons/shield.svg";
import { ReactComponent as LockIcon } from "component-library/src/images/svg/icons/lock.svg";
import IgConnectImage from "component-library/src/images/ig-connect-pop-up.jpg";
import { IgConnectImageProfileRedesign } from "assets";
import { useGetIgApiAuthUrl } from "../hooks/useGetIgApiAuthUrl";

interface Props {
  profileId?: string;
  close: () => void;
  isProfileDesign?: boolean;
}

const InstagramIntegrationPopUp = ({
  profileId,
  close,
  isProfileDesign,
}: Props) => {
  const { t } = useTranslation();

  const { data, isLoading, error } = useGetIgApiAuthUrl(profileId);

  const closeErrorPopup = () => {
    close();
  };

  if (error)
    return (
      <CommonErrorPopup
        errorStatusCode={error?.response?.status}
        popupProps={{
          close: closeErrorPopup,
        }}
        alertMessageProps={{
          buttons: [
            {
              type: "primary",
              name: t("common:button.label.close"),
              click: closeErrorPopup,
            },
          ],
        }}
        errorCode={AuthInstagramIntegrationCode.Connect}
      />
    );

  return (
    <Popup width={{ lg: 6, md: 6 }} close={close} priority="high">
      <div className="g-popup-content dark:text-black">
        <div className="g-popup-content__header">
          {t("performer:profile.socialMedia.instagram.popup.title")}
        </div>
        <div className="g-popup-content">
          <div className="c-applied-filters-popup__content px-4">
            <p className="c-font-primary my-2.5">
              {t("performer:profile.socialMedia.instagram.popup.descr")}
            </p>
            <div className="flex flex-col gap-5 py-5 md:flex-row md:gap-2">
              <div className="basis-1/2">
                <img
                  className="rounded-[20px]"
                  src={
                    isProfileDesign
                      ? IgConnectImageProfileRedesign
                      : IgConnectImage
                  }
                  alt={t("performer:profile.socialMedia.instagram.popup.title")}
                />
              </div>
              <div className="flex basis-1/2 flex-col gap-4 pl-2">
                <div>
                  <p className="c-font-primary flex gap-2 font-semibold">
                    <ShieldIcon height={24} width={24} />
                    {t("performer:profile.socialMedia.instagram.account.title")}
                  </p>
                  <p className="c-font-primary">
                    {t("performer:profile.socialMedia.instagram.account.text")}
                  </p>
                </div>
                <div>
                  <p className="c-font-primary flex gap-2 font-semibold">
                    <LockIcon height={24} width={24} />
                    {t("performer:profile.socialMedia.instagram.privacy.title")}
                  </p>
                  <p className="c-font-primary">
                    <Trans
                      i18nKey="performer:profile.socialMedia.instagram.privacy.text"
                      components={{
                        a: (
                          <a
                            href={ExternalLinks.SpotlightTermsAndConditions}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Terms and conditions"
                          />
                        ),
                      }}
                    />{" "}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr className="g-split-line" />
        <div className="g-popup-content__buttons-set flex justify-between">
          <div>
            <Button
              type="secondary"
              text={t("common:button.label.cancel")}
              onClick={close}
            />
          </div>
          <div>
            <Button
              type="primary"
              text={t("performer:profile.socialMedia.instagram.submit")}
              onClick={() => {
                window.open(data, "ig-login", "width=500,height=700");
              }}
              loading={isLoading}
            />
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default InstagramIntegrationPopUp;
